import { FC, useCallback, useEffect } from 'react';
import { Form } from 'react-final-form';

import { Panel } from 'src/components/Panel/Panel';
import { savePlace } from 'src/store/admin/services';
import { adminActions } from 'src/store/admin/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Desk } from 'src/types';

import { Actions } from './Actions';
import { DeskFields } from './Fields/DeskFields';

export interface DeskSidesheetProps {
  bookingObject: Desk;
}

export const DeskSidesheet: FC<DeskSidesheetProps> = ({ bookingObject }) => {
  const dispatch = useAppDispatch();

  const save = useCallback(async () => {
    await dispatch(savePlace());
    return true;
  }, [dispatch]);

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      type ArrowKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown';

      const arrows = new Set<ArrowKey | string>([
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ]);
      const rotateHandlers: Record<ArrowKey, () => void> = {
        ArrowLeft: () => {
          dispatch(adminActions.update({ rotation: 180 }));
        },
        ArrowRight: () => {
          dispatch(adminActions.update({ rotation: 0 }));
        },
        ArrowUp: () => {
          dispatch(adminActions.update({ rotation: 270 }));
        },
        ArrowDown: () => {
          dispatch(adminActions.update({ rotation: 90 }));
        },
      };
      const moveHandlers: Record<ArrowKey, () => void> = {
        ArrowLeft: () => {
          dispatch(adminActions.moveLeft());
        },
        ArrowRight: () => {
          dispatch(adminActions.moveRight());
        },
        ArrowUp: () => {
          dispatch(adminActions.moveUp());
        },
        ArrowDown: () => {
          dispatch(adminActions.moveDown());
        },
      };

      if (arrows.has(event.key)) {
        const key = event.key as ArrowKey;
        if (event.shiftKey) {
          rotateHandlers[key]();
        } else {
          moveHandlers[key]();
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);

  return (
    <Form onSubmit={save} initialValues={bookingObject}>
      {() => (
        <Panel
          actions={<Actions existing={!!bookingObject.id} />}
          testId='administratorSidesheetPanel'
        >
          <DeskFields bookingObject={bookingObject} />
        </Panel>
      )}
    </Form>
  );
};
